import { DispatchHelpMessage } from '@numeo/types'
import { useState } from 'react'
import { ChevronDown, ChevronUp, Phone } from 'lucide-react'
import { callDuration } from '../callDuration'
import { RecordingPlayer } from './RecordingPlayer'

type VoiceSession = NonNullable<DispatchHelpMessage['voiceSession']>

// Calls with more turns than this default to collapsed — short exchanges just render open, no toggle.
const SHORT_TRANSCRIPT_THRESHOLD = 3

/** Voice-call record → a compact call-log card (parity with the mobile), never the raw JSON. */
export function VoiceCallCard({ voiceSession, tabId, messageId }: { voiceSession: VoiceSession; tabId: string; messageId: string }) {
    const isLong = voiceSession.transcript.length > SHORT_TRANSCRIPT_THRESHOLD
    const [expanded, setExpanded] = useState(!isLong)
    const dur = callDuration(voiceSession.startedAt, voiceSession.endedAt)

    return (
        <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                    <Phone className="h-3.5 w-3.5" /> Voice call{dur ? ` · ${dur}` : ''}
                </div>
                {voiceSession.hasRecording && <RecordingPlayer key={`${tabId}:${messageId}`} tabId={tabId} messageId={messageId} />}
                {isLong && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="mt-2 flex items-center gap-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300"
                    >
                        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {expanded ? 'Hide transcript' : `Show transcript (${voiceSession.transcript.length})`}
                    </button>
                )}
                {expanded && (
                    <div className="mt-1.5 max-h-72 space-y-1 overflow-y-auto text-sm">
                        {voiceSession.transcript.map((t, i) => (
                            <div key={i} className={t.role === 'user' ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
                                <span className="font-medium">{t.role === 'user' ? 'Driver' : 'AI'}:</span> {t.content}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
